import { useState, useEffect, useRef } from 'react';
import api from '../../services/api';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import { Select } from '../../components/ui/Input';
import Badge from '../../components/ui/Badge';
import Table, { THead, TH, TBody, TR, TD } from '../../components/ui/Table';
import toast from 'react-hot-toast';

export default function ImportExcel() {
  const [orgs, setOrgs] = useState([]);
  const [organizationId, setOrganizationId] = useState('');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [activeSheet, setActiveSheet] = useState('');
  const [uploading, setUploading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileRef = useRef(null);

  useEffect(() => {
    api.get('/admin/organizations')
      .then(({ data }) => setOrgs(data.organizations))
      .catch(console.error);
  }, []);

  const handleUpload = async () => {
    if (!file) return toast.error('Please select a file');
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/admin/import/preview', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(data);
      const sheets = Object.keys(data.sheets);
      if (sheets.length > 0) setActiveSheet(sheets[0]);
      toast.success('File parsed successfully');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to parse file');
    } finally {
      setUploading(false);
    }
  };

  const handleConfirm = async () => {
    if (!organizationId) return toast.error('Please select an organization');
    setConfirming(true);
    try {
      const { data } = await api.post('/admin/import/confirm', {
        sheets: preview.sheets,
        organizationId: parseInt(organizationId, 10),
      });
      setImportResult(data.summary);
      toast.success('Import completed!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally {
      setConfirming(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setActiveSheet('');
    setImportResult(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Success state
  if (importResult) {
    return (
      <div>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-100">Import Complete</h1>
        </div>
        <Card className="max-w-lg text-center py-8">
          <div className="w-16 h-16 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-[18px] font-semibold text-[#e2e8f0] mb-4">Successfully Imported</h3>
          <div className="flex justify-center gap-6 mb-6">
            <div>
              <p className="text-2xl font-bold text-[#06b6d4]">{importResult.productLines}</p>
              <p className="text-[11px] text-[#64748b]">Product Lines</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-[#06b6d4]">{importResult.appliances}</p>
              <p className="text-[11px] text-[#64748b]">Appliances</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-[#06b6d4]">{importResult.licenses}</p>
              <p className="text-[11px] text-[#64748b]">Licenses</p>
            </div>
          </div>
          <Button onClick={reset}>Import Another File</Button>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-100">Bulk Import from Excel</h1>
            <p className="text-slate-400 mt-1">Upload an .xlsx file to import appliances and licenses in bulk</p>
          </div>
          <Button variant="ghost" onClick={() => { window.location.href = '/api/admin/import/template'; }}>
            <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
            Download Template
          </Button>
        </div>
      </div>

      {/* Step 1: Upload */}
      <Card className="mb-6">
        <h3 className="text-[14px] font-semibold text-[#e2e8f0] mb-4">Step 1: Select Organization & Upload File</h3>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <Select
            label="Organization"
            value={organizationId}
            onChange={(e) => setOrganizationId(e.target.value)}
          >
            <option value="">Select organization...</option>
            {orgs.map(o => <option key={o.id} value={o.id}>{o.shortName || o.name}</option>)}
          </Select>
          <div>
            <label className="block text-[13px] font-medium text-[#94a3b8] mb-1.5">Excel File (.xlsx)</label>
            <input
              ref={fileRef}
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files[0])}
              className="w-full text-[13px] text-[#94a3b8] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-[12px] file:font-semibold file:bg-[#06b6d4] file:text-[#060a10] hover:file:bg-[#0ea5c7] file:cursor-pointer"
            />
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={handleUpload} disabled={!file || uploading}>
            {uploading ? 'Parsing...' : 'Upload & Preview'}
          </Button>
          <div className="text-[11px] text-[#475569]">
            Format: Each sheet = product line. Columns: Unit Name, Serial Number, License 1 Key, License 1 Name, License 2 Key, License 2 Name, License 3 Key, License 3 Name
          </div>
        </div>
      </Card>

      {/* Step 2: Preview */}
      {preview && (
        <Card className="mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-[14px] font-semibold text-[#e2e8f0]">Step 2: Preview Data</h3>
            <div className="flex gap-2">
              <Badge color="cyan">{preview.summary.productLines} product lines</Badge>
              <Badge color="green">{preview.summary.appliances} appliances</Badge>
              <Badge color="purple">{preview.summary.licenses} licenses</Badge>
            </div>
          </div>

          {/* Sheet tabs */}
          <div className="flex gap-1 mb-4 border-b border-[rgba(148,163,184,0.06)]">
            {Object.keys(preview.sheets).map(name => (
              <button
                key={name}
                onClick={() => setActiveSheet(name)}
                className={`px-4 py-2 text-[12px] font-medium transition-colors border-b-2 -mb-px ${
                  activeSheet === name
                    ? 'text-[#06b6d4] border-[#06b6d4]'
                    : 'text-[#64748b] border-transparent hover:text-[#94a3b8]'
                }`}
              >
                {name} ({preview.sheets[name].length})
              </button>
            ))}
          </div>

          {/* Table for active sheet */}
          {activeSheet && preview.sheets[activeSheet] && (
            <Table>
              <THead>
                <tr>
                  <TH>#</TH>
                  <TH>Unit Name</TH>
                  <TH>Serial Number</TH>
                  <TH>License 1</TH>
                  <TH>License 2</TH>
                  <TH>License 3</TH>
                </tr>
              </THead>
              <TBody>
                {preview.sheets[activeSheet].map((row, i) => (
                  <TR key={i}>
                    <TD>{i + 1}</TD>
                    <TD><span className="text-[#e2e8f0] font-medium">{row.unitName}</span></TD>
                    <TD><span className="font-mono text-[11px]">{row.serialNumber}</span></TD>
                    <TD>
                      <p className="text-[10px] text-[#475569]">{row.license1Name}</p>
                      <p className="font-mono text-[10px] text-[#06b6d4]">{row.license1Key}</p>
                    </TD>
                    <TD>
                      <p className="text-[10px] text-[#475569]">{row.license2Name}</p>
                      <p className="font-mono text-[10px] text-[#06b6d4]">{row.license2Key}</p>
                    </TD>
                    <TD>
                      <p className="text-[10px] text-[#475569]">{row.license3Name}</p>
                      <p className="font-mono text-[10px] text-[#06b6d4]">{row.license3Key}</p>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          )}

          {/* Step 3: Confirm */}
          <div className="mt-6 flex items-center gap-3">
            <Button onClick={handleConfirm} disabled={confirming || !organizationId}>
              {confirming ? 'Importing...' : 'Confirm & Import'}
            </Button>
            <Button variant="secondary" onClick={reset}>Cancel</Button>
            {!organizationId && (
              <span className="text-[11px] text-amber-400">Please select an organization above first</span>
            )}
          </div>
        </Card>
      )}
    </div>
  );
}
